import { Form, Button, Alert } from 'react-bootstrap';
import { connect } from 'react-redux';
import axiosInstance from '../general/axiosInstance';
import React, { useEffect, useState, useMemo } from 'react';
import { setIsLoading } from '../general/actions';
import { currencyFormater } from '../general/utils';
import AlertModal from '../general/alertModal';
import FundUsers from './fundUsers';
import SelectAgreement from '../general/selectAgreement';
import FundsList from './fundsList';
import { C, barColor } from '../general/colors';

function Funds(props) {
    const { setIsLoading } = props;

    const [productTypes, setProductTypes] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [isCreation, setIsCreation] = useState(false);
    const [hasUsers, setHasUsers] = useState(false);

    const [alertModalShow, setAlertModalShow] = useState(false);
    const [alertModalMsg, setAlertModalMsg] = useState('');
    const [alertModalType, setAlertModalType] = useState('');
    const [alertModalOnConfirm, setAlertModalOnConfirm] = useState(null);
    const [alertModalOnClose, setAlertModalOnClose] = useState(null);

    const [idEdit, setIdEdit]       = useState('');
    const [agreement, setAgreement] = useState('');
    const [extId, setExtId]         = useState('');
    const [name, setName]           = useState('');
    const [description, setDescription] = useState('');
    const [productType, setProductType] = useState('');
    const [department, setDepartment]   = useState('');
    const [isActive, setIsActive]       = useState(true);
    const [amount, setAmount]           = useState(0);
    const [spent, setSpent]             = useState(0);
    const available = useMemo(() => amount - spent, [amount, spent]);

    const [message, setMessage]   = useState('');
    const [messageType, setMessageType] = useState('warning');

    useEffect(() => {
        setMessageType('warning');
        if (!extId) return setMessage('Ext Id es obligatorio');
        if (!name) return setMessage('Nombre es obligatorio');
        if (!agreement) return setMessage('Contrato es obligatorio');
        if (isNaN(amount) || amount <= 0) return setMessage('Valor inicial inválido');
        if (!department) return setMessage('Centro de costo es obligatorio');
        if (amount < spent) return setMessage('Valor inicial no puede ser menor que el consumo');
        setMessage('');
    }, [extId, name, agreement, department, amount]);

    useEffect(() => {
        if (isCreation) {
            setIdEdit(''); setName(''); setDescription(''); setExtId('');
            setIsActive(true); setAmount(0); setSpent(0); setProductType(''); setDepartment('');
        }
    }, [isCreation]);

    const getProductTypes = () => {
        setIsLoading(true);
        axiosInstance.get('/product-types')
            .then(res => { setProductTypes(res.data); setIsLoading(false); })
            .catch(() => setIsLoading(false));
    };

    const getDepartments = () => {
        setIsLoading(true);
        axiosInstance.get('/departments/agreements/' + agreement)
            .then(res => { setDepartments(res.data); setIsLoading(false); })
            .catch(() => setIsLoading(false));
    };

    useEffect(() => { if (agreement) { getDepartments(); getProductTypes(); } }, [agreement]);

    const getItemData = () => ({
        ext_id: extId, name, agreement_id: agreement, description,
        amount, is_active: isActive, product_type_id: productType, department_id: department,
    });

    const deleteItem = async (id, confirmed) => {
        if (!confirmed) {
            setAlertModalShow(true);
            setAlertModalMsg('¿Está seguro de eliminar el fondo ' + id + '?');
            setAlertModalOnConfirm(() => () => { deleteItem(id, true); });
            return;
        }
        setIsLoading(true);
        try {
            await axiosInstance.delete(`/funds/${id}`);
            setAlertModalShow(true);
            setAlertModalMsg('Fondo eliminado correctamente');
            setAlertModalOnClose(() => () => { setIdEdit(''); setIsCreation(false); });
        } catch (error) {
            setAlertModalShow(true);
            setAlertModalType('danger');
            if (error.response.data.message.indexOf('Fund is used') > 0) setAlertModalMsg('No se puede eliminar: tiene consumo');
            else if (error.response.data.message.indexOf('user_funds_fund_id_foreign') > 0) setAlertModalMsg('No se puede eliminar: tiene usuarios asignados');
            else setAlertModalMsg(error.message);
        } finally { setIsLoading(false); }
    };

    const saveItem = () => {
        setIsLoading(true);
        const baseUrl = isCreation ? '/funds' : '/funds/' + idEdit;
        axiosInstance.get(baseUrl + '/agreements/' + agreement + '/ext-id/' + extId)
            .then(res => {
                setIsLoading(false);
                if (res.data.length) {
                    setAlertModalShow(true);
                    setAlertModalType('danger');
                    setAlertModalMsg('Ext ID ya existe en otro fondo del contrato');
                } else {
                    if (isCreation) createItem(); else updateItem();
                }
            })
            .catch(() => setIsLoading(false));
    };

    const createItem = async () => {
        try {
            await axiosInstance.post('/funds', getItemData());
            setIsCreation(false);
        } catch (error) {
            setMessageType('danger');
            setMessage(error.response.data.message.indexOf('Duplicate') > 0
                ? 'Ext ID ya existe en este contrato' : error.message);
        } finally { setIsLoading(false); }
    };

    const updateItem = async () => {
        try {
            await axiosInstance.put(`/funds/${idEdit}`, getItemData());
            setIdEdit('');
        } catch (error) {
            setMessageType('danger');
            setMessage(error.response.data.message.indexOf('Duplicate') > 0
                ? 'Ext ID ya existe' : error.message);
        } finally { setIsLoading(false); }
    };

    const setEditData = (row) => {
        setIdEdit(row.id); setName(row.name); setAgreement(row.agreement_id);
        setDescription(row.description); setExtId(row.ext_id); setIsActive(row.is_active);
        setAmount(parseFloat(isNaN(row.amount) ? 0 : row.amount)); setSpent(parseFloat(row.spent));
        setProductType(row.product_type_id);
        setDepartment(row.department?.id || '');
    };

    const isFormOpen = idEdit || isCreation;
    const pct = amount > 0 ? Math.min(100, (spent / amount) * 100) : 0;
    const fundBarColor = barColor(pct);

    return (
        <>
            {!isFormOpen && (
                <FundsList isCreation={isCreation} setIsCreation={setIsCreation}
                    setEditData={setEditData} setAgreement={setAgreement} agreement={agreement} />
            )}

            {isFormOpen && (
                <>
                    <div className="order-page-header">
                        <div className="order-page-title">
                            <i className="fa fa-money" style={{ color: 'var(--abi-primary)' }} />
                            {idEdit ? `Modificar Fondo #${idEdit}: ${extId}` : 'Crear Fondo'}
                        </div>
                        {idEdit && (
                            <Button size="sm" variant="outline-danger"
                                disabled={spent > 0 || hasUsers}
                                onClick={() => deleteItem(idEdit)}>
                                <i className="fa fa-trash" /> Eliminar
                            </Button>
                        )}
                    </div>

                    {(spent > 0 || hasUsers) && (
                        <Alert variant="warning" className="py-2" style={{ fontSize: 13 }}>
                            No se puede eliminar: tiene consumo o usuarios asignados
                        </Alert>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16, alignItems: 'start' }}>
                        {/* Form */}
                        <div>
                            <div className="order-section">
                                <div className="order-section-header"><i className="fa fa-file-text-o" /> Contrato y datos del fondo</div>
                                <div className="order-section-body">
                                    <div className="mb-3">
                                        <SelectAgreement agreement={agreement} setAgreement={setAgreement} disabled={true} />
                                    </div>
                                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                                        <div style={{ flex: 1, minWidth: 140 }}>
                                            <Form.Label className="report-field-label">Ext ID</Form.Label>
                                            <Form.Control type="text" value={extId} placeholder="ID único del fondo"
                                                onChange={(e) => setExtId(e.target.value)} />
                                        </div>
                                        <div style={{ flex: 2, minWidth: 180 }}>
                                            <Form.Label className="report-field-label">Nombre</Form.Label>
                                            <Form.Control type="text" value={name}
                                                onChange={(e) => setName(e.target.value)} />
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 12 }}>
                                        <div style={{ flex: 1, minWidth: 160 }}>
                                            <Form.Label className="report-field-label">Tipo de Producto</Form.Label>
                                            <Form.Select value={productType} onChange={(e) => setProductType(e.target.value)} disabled>
                                                <option value="0">Todos</option>
                                                {productTypes.map(pt => (
                                                    <option key={pt.id} value={pt.id}>{pt.id}: {pt.name}</option>
                                                ))}
                                            </Form.Select>
                                        </div>
                                        <div style={{ flex: 1, minWidth: 160 }}>
                                            <Form.Label className="report-field-label">Centro de costos</Form.Label>
                                            <Form.Select value={department} disabled={spent > 0 && hasUsers}
                                                onChange={(e) => setDepartment(e.target.value)}>
                                                <option value={0}>
                                                    {!agreement ? '— Selecciona un contrato primero —' : '— Selecciona un centro —'}
                                                </option>
                                                {departments.map(d => (
                                                    <option key={d.id} value={d.id}>{d.id}: {d.name}</option>
                                                ))}
                                            </Form.Select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="order-section">
                                <div className="order-section-header"><i className="fa fa-bar-chart" /> Presupuesto</div>
                                <div className="order-section-body">
                                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
                                        <div style={{ flex: 1 }}>
                                            <Form.Label className="report-field-label">Valor inicial</Form.Label>
                                            <Form.Control type="number" value={amount}
                                                onChange={(e) => setAmount(e.target.value)} />
                                            <div style={{ fontSize: 11, color: 'var(--abi-muted)', marginTop: 3 }}>{currencyFormater(amount)}</div>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <Form.Label className="report-field-label">Consumo</Form.Label>
                                            <Form.Control disabled type="text" value={currencyFormater(spent)} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <Form.Label className="report-field-label">Disponible</Form.Label>
                                            <Form.Control disabled type="text"
                                                value={currencyFormater(available)}
                                                style={{ color: available < 0 ? C.danger : C.success, fontWeight: 700 }} />
                                        </div>
                                    </div>
                                    {idEdit && (
                                        <div>
                                            <div className="report-fund-bar-wrap">
                                                <div className="report-fund-bar-fill" style={{ width: `${pct}%`, background: fundBarColor }} />
                                            </div>
                                            <div style={{ fontSize: 11, color: fundBarColor, fontWeight: 700, marginTop: 4 }}>
                                                {pct.toFixed(0)}% consumido
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="order-section">
                                <div className="order-section-header"><i className="fa fa-sticky-note-o" /> Descripción y estado</div>
                                <div className="order-section-body">
                                    <Form.Control as="textarea" rows={3} value={description}
                                        placeholder="Descripción del fondo (opcional)"
                                        onChange={(e) => setDescription(e.target.value)}
                                        className="mb-3" />
                                    <Form.Check type="switch" id="fund-active"
                                        label="Activo para pedidos nuevos"
                                        checked={isActive}
                                        onChange={(e) => setIsActive(e.target.checked)} />
                                </div>
                            </div>

                            {message && (
                                <Alert variant={messageType} className="py-2" style={{ fontSize: 13 }}>{message}</Alert>
                            )}

                            <div className="order-form-footer">
                                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                                    <Button variant="outline-secondary" size="sm"
                                        onClick={() => { setIdEdit(''); setIsCreation(false); }}>
                                        <i className="fa fa-arrow-left" /> Volver
                                    </Button>
                                    <Button variant="primary" size="sm" onClick={saveItem} disabled={!!message}>
                                        <i className="fa fa-save" /> Guardar
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Sidebar users */}
                        <div className="order-section">
                            <div className="order-section-header"><i className="fa fa-users" /> Usuarios asignados</div>
                            <div className="order-section-body">
                                <FundUsers fundId={idEdit} agreementId={agreement} department={department} setHasUsers={setHasUsers} />
                            </div>
                        </div>
                    </div>
                </>
            )}

            <AlertModal
                show={alertModalShow} setShow={setAlertModalShow}
                msg={alertModalMsg} setMsg={setAlertModalMsg}
                onConfirm={alertModalOnConfirm} setOnConfirm={setAlertModalOnConfirm}
                onClose={alertModalOnClose} setOnClose={setAlertModalOnClose}
                variant={alertModalType}
            />
        </>
    );
}

function mapStateToProps(state) { return { isLoading: state.isLoading }; }
const mapDispatchToProps = { setIsLoading };
export default connect(mapStateToProps, mapDispatchToProps)(Funds);
